/**
 * AIF-01 — client-side parser for the "Import classes" dialog.
 *
 * Parses a YOLO `classes.txt` or a `data.yaml` `names:` block into an ordered
 * list of `{ index, name }` rows so the dialog can PREVIEW the index↔name
 * mapping (and compute created/updated/unchanged against the loaded classes)
 * BEFORE sending the RAW content to the server. The server is authoritative —
 * this parser only mirrors the BE's hand-rolled parser closely enough for an
 * accurate preview; the BE returns the real counts on confirm.
 *
 * Supported forms (matches `.vibe/23` AIF-01 BE resolution + contract):
 *
 *   classes.txt
 *     • plain lines, line-ordinal = index:        LE_erosion\n crack\n …
 *     • `index name` lines:                       0 LE_erosion\n 1 crack\n …
 *
 *   data.yaml  `names:`
 *     • inline list:    names: [LE_erosion, crack]
 *     • inline map:     names: {0: LE_erosion, 1: crack}
 *     • block list:     names:\n   - LE_erosion\n   - crack
 *     • block map:      names:\n   0: LE_erosion\n   1: crack
 *
 * Blank lines and `#` comments are ignored. The detected format is returned so
 * the dialog can show "Detected: YOLO data.yaml `names:`" etc., matching the
 * `format` it forwards to the server.
 */
import type { ImportAiModelClassesFormat } from "@aeriosiq/types";

export interface ParsedClassRow {
  index: number;
  name: string;
}

export interface ParseClassListResult {
  rows: ParsedClassRow[];
  /** The concrete format detected (never `auto`). */
  format: Exclude<ImportAiModelClassesFormat, "auto">;
}

export class ParseClassListError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ParseClassListError";
  }
}

/** Strip a trailing `# comment` that is not inside quotes (good enough for
 * the simple values these files carry). */
function stripComment(line: string): string {
  const hash = line.indexOf("#");
  return hash === -1 ? line : line.slice(0, hash);
}

/** Remove surrounding single/double quotes from a scalar value. */
function unquote(raw: string): string {
  const s = raw.trim();
  if (s.length >= 2) {
    const first = s[0];
    const last = s[s.length - 1];
    if ((first === '"' && last === '"') || (first === "'" && last === "'")) {
      return s.slice(1, -1);
    }
  }
  return s;
}

/** True when the content looks like a YOLO `data.yaml` (`names:` key). */
function looksLikeDataYaml(content: string): boolean {
  return /^\s*names\s*:/m.test(content);
}

/**
 * Parse the `names:` payload of a YOLO `data.yaml` into ordered rows. Handles
 * the four shapes (inline list, inline map, block list, block map). Throws
 * `ParseClassListError` on a malformed or empty `names:` section.
 */
function parseDataYaml(content: string): ParsedClassRow[] {
  const lines = content.split(/\r?\n/);
  let namesLineIdx = -1;
  let inlineRest = "";
  for (let i = 0; i < lines.length; i++) {
    const m = /^(\s*)names\s*:(.*)$/.exec(lines[i] ?? "");
    if (m) {
      namesLineIdx = i;
      inlineRest = (m[2] ?? "").trim();
      break;
    }
  }
  if (namesLineIdx === -1) {
    throw new ParseClassListError("No `names:` key found in the YAML.");
  }

  // -- Inline forms on the same line as `names:` --------------------------
  if (inlineRest.length > 0) {
    // Inline list: names: [a, b, c]
    if (inlineRest.startsWith("[") && inlineRest.endsWith("]")) {
      const inner = inlineRest.slice(1, -1).trim();
      if (inner.length === 0) {
        throw new ParseClassListError("`names:` list is empty.");
      }
      const parts = inner
        .split(",")
        .map((p) => unquote(p))
        .filter((p) => p.length > 0);
      if (parts.length === 0) {
        throw new ParseClassListError("`names:` list is empty.");
      }
      return parts.map((name, index) => ({ index, name }));
    }
    // Inline map: names: {0: a, 1: b}
    if (inlineRest.startsWith("{") && inlineRest.endsWith("}")) {
      const inner = inlineRest.slice(1, -1).trim();
      if (inner.length === 0) {
        throw new ParseClassListError("`names:` map is empty.");
      }
      const rows: ParsedClassRow[] = [];
      for (const pair of inner.split(",")) {
        const colon = pair.indexOf(":");
        if (colon === -1) {
          throw new ParseClassListError(
            `Malformed map entry in \`names:\`: "${pair.trim()}".`,
          );
        }
        const idxRaw = unquote(pair.slice(0, colon));
        const name = unquote(pair.slice(colon + 1));
        const index = Number(idxRaw);
        if (!Number.isInteger(index) || index < 0) {
          throw new ParseClassListError(
            `Invalid class index "${idxRaw}" in \`names:\`.`,
          );
        }
        if (name.length === 0) {
          throw new ParseClassListError(
            `Empty class name for index ${String(index)}.`,
          );
        }
        rows.push({ index, name });
      }
      return rows;
    }
    throw new ParseClassListError(
      "Unrecognized inline `names:` value (expected [...] or {...}).",
    );
  }

  // -- Block forms on the following indented lines ------------------------
  const blockRows: ParsedClassRow[] = [];
  let ordinal = 0;
  let isMap: boolean | null = null;
  for (let i = namesLineIdx + 1; i < lines.length; i++) {
    const rawLine = lines[i] ?? "";
    // A non-indented, non-blank line ends the block.
    if (rawLine.trim().length > 0 && !/^\s/.test(rawLine)) break;
    const line = stripComment(rawLine).trim();
    if (line.length === 0) continue;

    if (line.startsWith("-")) {
      // Block list item: `- name`
      if (isMap === true) {
        throw new ParseClassListError("Mixed list/map entries under `names:`.");
      }
      isMap = false;
      const name = unquote(line.slice(1));
      if (name.length === 0) {
        throw new ParseClassListError("Empty class name in `names:` list.");
      }
      blockRows.push({ index: ordinal++, name });
      continue;
    }

    // Block map item: `0: name`
    const colon = line.indexOf(":");
    if (colon === -1) {
      throw new ParseClassListError(
        `Malformed entry under \`names:\`: "${line}".`,
      );
    }
    if (isMap === false) {
      throw new ParseClassListError("Mixed list/map entries under `names:`.");
    }
    isMap = true;
    const idxRaw = unquote(line.slice(0, colon));
    const name = unquote(line.slice(colon + 1));
    const index = Number(idxRaw);
    if (!Number.isInteger(index) || index < 0) {
      throw new ParseClassListError(
        `Invalid class index "${idxRaw}" under \`names:\`.`,
      );
    }
    if (name.length === 0) {
      throw new ParseClassListError(
        `Empty class name for index ${String(index)}.`,
      );
    }
    blockRows.push({ index, name });
  }

  if (blockRows.length === 0) {
    throw new ParseClassListError("`names:` section is empty.");
  }
  return blockRows;
}

/**
 * Parse a plain `classes.txt`. Each non-blank line is either `name`
 * (line-ordinal = index) or `index name`. The form is decided by the FIRST
 * data line and enforced for the rest so a mixed file is rejected.
 */
function parseClassesTxt(content: string): ParsedClassRow[] {
  const dataLines: string[] = [];
  for (const raw of content.split(/\r?\n/)) {
    const line = stripComment(raw).trim();
    if (line.length > 0) dataLines.push(line);
  }
  if (dataLines.length === 0) {
    throw new ParseClassListError("No class names found.");
  }

  // `index name` form iff EVERY line begins with an integer followed by
  // whitespace and a non-empty name. Otherwise treat as plain (ordinal).
  const indexNameRe = /^(\d+)\s+(\S.*)$/;
  const allIndexName = dataLines.every((l) => indexNameRe.test(l));

  if (allIndexName) {
    return dataLines.map((l) => {
      const m = indexNameRe.exec(l);
      // `allIndexName` already guarantees the regex matches every line.
      const idx = m?.[1] ?? "0";
      const name = m?.[2] ?? "";
      return { index: Number(idx), name: unquote(name) };
    });
  }

  // Plain form: the whole line is the name; ordinal is the index. Reject if
  // any line looks half-formed as `index name` (ambiguous file).
  return dataLines.map((name, index) => ({ index, name: unquote(name) }));
}

/**
 * Parse a class list payload into ordered, validated `{ index, name }` rows.
 *
 * @param content raw file text.
 * @param format  `auto` (default) detects YAML on a `names:` key; otherwise
 *                forces the named parser.
 * @throws ParseClassListError on malformed input, an empty result, or a
 *         duplicate index / name.
 */
export function parseClassList(
  content: string,
  format: ImportAiModelClassesFormat = "auto",
): ParseClassListResult {
  const resolved: Exclude<ImportAiModelClassesFormat, "auto"> =
    format === "auto"
      ? looksLikeDataYaml(content)
        ? "data_yaml"
        : "classes_txt"
      : format;

  const rows =
    resolved === "data_yaml"
      ? parseDataYaml(content)
      : parseClassesTxt(content);

  if (rows.length === 0) {
    throw new ParseClassListError("No classes found in the file.");
  }

  // Reject duplicate indices and names (the server rejects these too).
  const seenIndex = new Set<number>();
  const seenName = new Set<string>();
  for (const r of rows) {
    if (seenIndex.has(r.index)) {
      throw new ParseClassListError(
        `Duplicate class index ${String(r.index)}.`,
      );
    }
    if (seenName.has(r.name)) {
      throw new ParseClassListError(`Duplicate class name "${r.name}".`);
    }
    seenIndex.add(r.index);
    seenName.add(r.name);
  }

  return { rows, format: resolved };
}
